import { Tabs } from "@base-ui/react";
import { Plus, X } from "lucide-react";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { sendLoadTrackingEvent } from "@/store/telemetryStore";
import { useLaunchContainer, useStartServer } from "@/utils/devServerUtils";
import { useCanvasId } from "@/utils/utils";
import { Button } from "../ui/button";
import { DevServer } from "./DevServer";
import { Loader } from "./Loader";
import { Terminal } from "./Terminal";

type Tab = "dev-server" | `terminal-${string}`;

const STORAGE_KEY = "console-v2-terminals";

function loadTerminalsFromStorage(): {
  terminals: Exclude<Tab, "dev-server">[];
  currentTab: Tab;
} {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        terminals: parsed.terminals ?? [],
        currentTab: parsed.currentTab ?? "dev-server",
      };
    }
  } catch {
    // Ignore parse errors
  }
  return { terminals: [], currentTab: "dev-server" };
}

function saveTerminalsToStorage(
  terminals: Exclude<Tab, "dev-server">[],
  currentTab: Tab,
) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ terminals, currentTab }),
    );
  } catch {
    // Ignore storage errors
  }
}

export const ConsoleV2 = () => {
  const [currentTab, setCurrentTab] = useState<Tab>(
    () => loadTerminalsFromStorage().currentTab,
  );
  const [terminals, setTerminals] = useState<Exclude<Tab, "dev-server">[]>(
    () => loadTerminalsFromStorage().terminals,
  );

  const canvasId = useCanvasId();

  // Persist terminals and current tab to sessionStorage
  useEffect(() => {
    saveTerminalsToStorage(terminals, currentTab);
  }, [terminals, currentTab]);

  // Dev server hooks
  const startServer = useStartServer();
  const launchContainer = useLaunchContainer();
  const devServerStatus = useSelector(
    (state: any) => state.codebase.devServerStatus,
  );

  // Button handlers
  const handleClear = useCallback(() => {
    // if (currentTab === "dev-server") {
    //   devServerRef.current?.clear();
    // } else {
    //   terminalRefs.current.get(currentTab)?.clear();
    // }
  }, []);

  const handleRestart = useCallback(() => {
    startServer({ forceRestart: true });
  }, [startServer]);

  const handleRelaunch = useCallback(async () => {
    const relaunchTraceId = crypto.randomUUID();
    const startMs = Date.now();

    await sendLoadTrackingEvent({
      eventType: "web_relaunch_container_from_console",
      traceId: relaunchTraceId,
      canvasId: canvasId || "",
      eventData: { startMs },
    });

    let { success, error } = await launchContainer({
      forceRelaunch: true,
      traceId: relaunchTraceId,
    });
    let startedServer = false;

    if (success) {
      const { success: serverSuccess, error: errorSuccess } = await startServer(
        {
          forceRestart: true,
          traceId: relaunchTraceId,
        },
      );
      startedServer = true;
      success = serverSuccess;
      error = errorSuccess;
    }

    await sendLoadTrackingEvent({
      eventType: "web_relaunch_container_from_console",
      traceId: relaunchTraceId,
      canvasId: canvasId || "",
      eventData: {
        startMs,
        endMs: Date.now(),
        success,
        error,
        startedServer,
      },
    });
  }, [canvasId, launchContainer, startServer]);

  // Close terminal tab handler
  const handleCloseTerminal = useCallback(
    (terminalId: Exclude<Tab, "dev-server">) => {
      setTerminals((prev) => prev.filter((t) => t !== terminalId));

      // Switch to another tab if closing the current one
      if (currentTab === terminalId) {
        setCurrentTab("dev-server");
      }
    },
    [currentTab],
  );

  // Terminal exit handler (auto-close on exit)
  const handleTerminalExit = useCallback(
    (terminalId: Exclude<Tab, "dev-server">) => {
      handleCloseTerminal(terminalId);
    },
    [handleCloseTerminal],
  );

  return (
    <Tabs.Root
      value={currentTab}
      onValueChange={setCurrentTab}
      defaultValue="dev-server"
      className="bg-secondary-100 h-full z-50 flex flex-col"
    >
      <Tabs.List className="flex items-center text-xs px-1.5 relative">
        <Tabs.Tab value="dev-server" className="p-1.5">
          Console
        </Tabs.Tab>
        {terminals.map((terminalId) => (
          <Tabs.Tab
            key={terminalId}
            value={terminalId}
            className="p-1 flex items-center gap-1 group"
          >
            Terminal
            <button
              type="button"
              aria-label="Close terminal"
              className="opacity-0 group-hover:opacity-100 hover:bg-secondary-500 rounded size-3 flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                handleCloseTerminal(terminalId);
              }}
            >
              <X role="presentation" className="size-2.5" />
            </button>
          </Tabs.Tab>
        ))}
        <Tabs.Indicator className="border-t-2 border-current absolute w-[--active-tab-width] translate-x-[--active-tab-left] left-0 bottom-0" />
        <button
          aria-label="New Terminal"
          type="button"
          className="hover:bg-current/20 size-4 p-0 flex items-center justify-center rounded hover:bg-secondary-500 ml-2"
          onClick={() => {
            const newTerminalId: Exclude<Tab, "dev-server"> =
              `terminal-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
            setTerminals((prev) => [...prev, newTerminalId]);
            setCurrentTab(newTerminalId);
          }}
        >
          <Plus role="presentation" className="size-3" />
        </button>
        <div className="ml-auto">
          <Button variant="ghost" size="xs" type="button" onClick={handleClear}>
            Clear
          </Button>
          <Button
            variant="ghost"
            size="xs"
            type="button"
            onClick={handleRestart}
          >
            {devServerStatus?.isServerRunning ? "Restart" : "Start"}
          </Button>
          <Button
            variant="ghost"
            size="xs"
            type="button"
            onClick={handleRelaunch}
          >
            Relaunch
          </Button>
        </div>
      </Tabs.List>
      <Suspense fallback={<Loader count={10} />}>
        <Tabs.Panel
          value="dev-server"
          className="grow shrink min-h-0 [data-hidden]:hidden grid grid-cols-1 grid-rows-1"
        >
          <DevServer />
        </Tabs.Panel>
        {terminals.map((terminalId) => (
          <Tabs.Panel
            key={terminalId}
            value={terminalId}
            className="grow shrink min-h-0 [data-hidden]:hidden grid grid-cols-1 grid-rows-1"
          >
            <Terminal
              key={terminalId}
              terminalId={terminalId}
              onExit={() => handleTerminalExit(terminalId)}
            />
          </Tabs.Panel>
        ))}
      </Suspense>
    </Tabs.Root>
  );
};
